import React, { useState, useEffect } from 'react';
import { getFormaPagoById, createFormaPago, updateFormaPago } from '../services/formaPagoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, CreditCard, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function FormaPagoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        activo: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            cargarFormaPago();
        }
    }, [id]);

    const cargarFormaPago = async () => {
        try {
            setLoading(true);
            const data = await getFormaPagoById(token, id);
            setFormData({
                nombre: data.nombre,
                descripcion: data.descripcion || '',
                activo: data.activo
            });
        } catch (error) {
            console.error('Error al cargar la forma de pago:', error);
            toast.error('Error al cargar los datos');
            navigate('/parametrizacion/formas-pago');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updateFormaPago(token, id, formData);
                toast.success('Cambios guardados');
            } else {
                await createFormaPago(token, formData);
                toast.success('Nuevo medio de pago registrado');
            }
            setTimeout(() => navigate('/parametrizacion/formas-pago'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error('Error al procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-4 flex items-center gap-3 text-white">
                <button
                    onClick={() => navigate('/parametrizacion/formas-pago')}
                    className="p-1.5 hover:bg-slate-600 rounded-sm transition-colors text-slate-300 hover:text-white"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 text-white">
                    <CreditCard className="w-5 h-5 text-blue-400" />
                    <div>
                        <h1 className="text-lg font-bold">
                            {isEditMode ? 'Mantenimiento: Editar Medio de Pago' : 'Mantenimiento: Nuevo Medio de Pago'}
                        </h1>
                        <span className="text-[0.6rem] uppercase tracking-[0.2em] text-slate-400 font-black">Parametrización Operativa</span>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-8">

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                        {/* Summary Column */}
                        <div className="md:col-span-4 bg-[#f8fafc] border border-slate-200 p-6 rounded-sm">
                            <h3 className="text-[0.65rem] font-black text-slate-500 uppercase tracking-widest mb-4 border-b border-slate-200 pb-2">Información del Módulo</h3>
                            <p className="text-xs text-slate-600 leading-relaxed mb-4">
                                Los medios de pago definen cómo ingresa el dinero a tesorería (Efectivo, Cheques, etc).
                            </p>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-[0.6rem] font-bold text-slate-400 uppercase">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                                    Auditoría Activa
                                </div>
                                <div className="flex items-center gap-2 text-[0.6rem] font-bold text-slate-400 uppercase">
                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div>
                                    Trazabilidad Contable
                                </div>
                            </div>
                        </div>

                        {/* Fields Column */}
                        <div className="md:col-span-8 space-y-6">
                            <div>
                                <label className="block text-[0.65rem] font-black text-slate-600 mb-2 uppercase tracking-widest">
                                    Nombre del Medio / Forma <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-4 py-2 text-sm border-b-2 border-slate-200 focus:border-blue-600 outline-none transition-all placeholder:text-gray-200 font-bold bg-transparent"
                                    placeholder="Ej: TRANSFERENCIA BANCARIA"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                                />
                            </div>

                            <div>
                                <label className="block text-[0.65rem] font-black text-slate-600 mb-2 uppercase tracking-widest">
                                    Descripción Operativa
                                </label>
                                <textarea
                                    className="w-full px-4 py-2 text-sm border border-slate-200 rounded-sm focus:ring-1 focus:ring-blue-500 outline-none transition-all h-24 bg-white"
                                    placeholder="Detalles adicionales sobre el uso de este medio..."
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                />
                            </div>

                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-100">
                                <div className="text-[0.65rem] font-black text-slate-600 uppercase tracking-widest">Estado del Registro</div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={formData.activo}
                                        onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-6 border-t border-slate-100 gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/formas-pago')}
                            className="px-6 py-2 text-[0.65rem] font-black uppercase tracking-widest border border-slate-300 text-slate-500 hover:bg-slate-50 transition-colors"
                        >
                            CANCELAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-10 py-2 text-[0.65rem] font-black uppercase tracking-widest bg-slate-800 text-white hover:bg-slate-900 transition-all flex items-center gap-2 shadow-lg shadow-slate-200 disabled:opacity-50"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {isEditMode ? 'ACTUALIZAR DATOS' : 'REGISTRAR MEDIO'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
